import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

export type DateRangePreset = 'today' | 'week' | 'month' | 'custom';

const BUSINESS_TIME_ZONE = 'America/Mexico_City';

export function businessDateInput(date = new Date()) {
  const parts = new Intl.DateTimeFormat('en-US', {
    day: '2-digit',
    month: '2-digit',
    timeZone: BUSINESS_TIME_ZONE,
    year: 'numeric',
  }).formatToParts(date);
  const year = parts.find((part) => part.type === 'year')?.value ?? '0000';
  const month = parts.find((part) => part.type === 'month')?.value ?? '01';
  const day = parts.find((part) => part.type === 'day')?.value ?? '01';
  return `${year}-${month}-${day}`;
}

export function dateRangeForPreset(preset: Exclude<DateRangePreset, 'custom'>, date = new Date()) {
  const today = businessDateInput(date);
  if (preset === 'today') return { from: today, to: today };
  const [year, month, day] = today.split('-').map(Number);
  if (preset === 'month') {
    return {
      from: `${year}-${String(month).padStart(2, '0')}-01`,
      to: new Date(Date.UTC(year, month, 0)).toISOString().slice(0, 10),
    };
  }
  const start = new Date(Date.UTC(year, month - 1, day));
  start.setUTCDate(start.getUTCDate() - ((start.getUTCDay() + 6) % 7));
  const end = new Date(start);
  end.setUTCDate(start.getUTCDate() + 6);
  return { from: start.toISOString().slice(0, 10), to: end.toISOString().slice(0, 10) };
}

export function mondayWeekRange(date = new Date()) {
  return dateRangeForPreset('week', date);
}

export function monthRange(date = new Date()) {
  return dateRangeForPreset('month', date);
}

export function toDateInput(date: Date) {
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 10);
}

export function isWithinDateRange(value: string | undefined, from: string, to: string) {
  if (!value) return false;
  const date = eventBusinessDateInput(value);
  return date >= from && date <= to;
}

export function overlapsDateRange(startValue: string | undefined, endValue: string | undefined, from: string, to: string) {
  if (!startValue && !endValue) return false;
  const start = startValue ? eventBusinessDateInput(startValue) : eventBusinessDateInput(endValue!);
  const end = endValue ? eventBusinessDateInput(endValue) : start;
  return start <= to && end >= from;
}

function eventBusinessDateInput(value: string) {
  if (!value.includes('T')) return value.slice(0, 10);
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value.slice(0, 10);
  const parts = new Intl.DateTimeFormat('en-US', {
    day: '2-digit',
    month: '2-digit',
    timeZone: BUSINESS_TIME_ZONE,
    year: 'numeric',
  }).formatToParts(date);
  const year = parts.find((part) => part.type === 'year')?.value ?? '0000';
  const month = parts.find((part) => part.type === 'month')?.value ?? '01';
  const day = parts.find((part) => part.type === 'day')?.value ?? '01';
  return `${year}-${month}-${day}`;
}

export function useUrlListFilters(defaults: Record<string, string>, options: { keepDefaults?: boolean } = {}) {
  const [params, setParams] = useSearchParams();
  const values = useMemo(() => {
    const next = { ...defaults };
    for (const key of Object.keys(defaults)) next[key] = params.get(key) ?? defaults[key];
    return next;
  }, [defaults, params]);

  const setValue = useCallback((key: string, value: string) => {
    setParams((current) => updateListSearchParams(current, defaults, { [key]: value }, options), { replace: true });
  }, [defaults, options.keepDefaults, setParams]);

  const setValues = useCallback((patch: Record<string, string>) => {
    setParams((current) => updateListSearchParams(current, defaults, patch, options), { replace: true });
  }, [defaults, options.keepDefaults, setParams]);

  return { values, setValue, setValues };
}

export function updateListSearchParams(
  current: URLSearchParams,
  defaults: Record<string, string>,
  patch: Record<string, string>,
  options: { keepDefaults?: boolean } = {},
) {
  const next = new URLSearchParams(current);
  for (const [key, value] of Object.entries(patch)) {
    if (!value || (!options.keepDefaults && value === defaults[key])) next.delete(key);
    else next.set(key, value);
  }
  if (!('page' in patch)) next.delete('page');
  return next;
}
